/* eslint-disable max-len */

import {
  cloneData, cloneDataOrNull, cloneListOfCloneable, DataModel, DataModelBuilder,
  isSameData, isSameList, SerializationContext, SerializedData,
} from '../../dto/dto.js';
import { IEntity } from '../../../components/core/entity.js';
import { ComponentList } from '../../../components/core/component-list.js';
import { TimeZone } from '../../../time/timezone.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { CustomerProfile } from '../customer-profile.js';
import { ContactDetails } from '../contact-details.js';
import { OnlinePaymentDetails } from '../online-payment-details.js';
import { PaymentType } from '../payment-type.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../../serialization/enum-utils.js';
import { PaymentStatus } from '../payment-status.js';
import { CustomerOrderBase } from '../../../contracts/customer-order.js';
import { VersionRange } from '../../core/version-range.js';
import { Language } from '../language.js';
import { PaymentMethod } from '../payment-method.js';
import { CustomerAccountBase } from '../customer-account-base.js';
import { TimeZoneRegion } from '../time-zone-region.js';
import { EntityMixin } from '../../../components/core/entity-mixin.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { OrderSource } from '../order-source.js';
import { OfflinePaymentDetails } from '../offline-payment-details.js';
import { OrderSideEffect } from '../orders/order-side-effect.js';
import { OrderSideEffectType } from '../orders/order-side-effect-type.js';
import { EventBookingStrategy } from '../event-booking-protocol/event-booking-strategy.js';
import { GenericOrderEntry, GenericOrderEntryStatus } from './generic-order-entry.js';
import { Country } from '../country.js';
import { Optional } from '../../core/optional.js';
import { Dayjs } from 'dayjs';
import { OrderDomain } from '../order-domain.js';

export enum GenericOrderStatus {
  Open = 'OPEN',
  Aborted = 'ABORTED',
  Canceled = 'CANCELED',
  Refunded = 'REFUNDED',
}

export class GenericOrder extends EntityMixin(DataModel) implements CustomerOrderBase, IEntity {
  static readonly TYPE_ID: string = '2a439cfd17744b7db542919ff74e882c';
  static readonly SCHEMA_VERSION: number = 1;

  public readonly userId: string | null;
  public readonly customerId: string | null;
  public readonly language: Language | null;
  public readonly country: Country;
  public readonly timezone: TimeZoneRegion;

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly source: OrderSource,
    public readonly domain: OrderDomain,
    public readonly businessRef: BusinessModuleRef,
    public readonly creationDate: Dayjs,
    public readonly customer: CustomerProfile | null,
    public readonly contactDetails: ContactDetails,
    public readonly status: GenericOrderStatus,
    public readonly entries: GenericOrderEntry[],
    public readonly paymentType: PaymentType | null,
    public readonly paymentMethod: PaymentMethod | null,
    public readonly onlinePaymentDetails: OnlinePaymentDetails | null,
    public readonly offlinePaymentDetails: OfflinePaymentDetails | null,
    public readonly components: ComponentList,
    public readonly sideEffects: OrderSideEffect[],
    public readonly isGuestOrder: boolean,
    public readonly cancelledDate: Dayjs | null,
    public readonly cancelledBy: string | null,
  ) {
    super();

    this.userId = customer?.userId ?? null;
    this.customerId = customer?.uniqueId ?? null;
    this.language = customer?.language ?? null;
    this.country = contactDetails.country;
    this.timezone = contactDetails.timezone;
  }

  get $typeId(): string {
    return GenericOrder.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(GenericOrder.SCHEMA_VERSION);
  }

  get isOpen(): boolean {
    return this.status === GenericOrderStatus.Open;
  }

  get isCanceled(): boolean {
    return this.status === GenericOrderStatus.Canceled;
  }

  static create(args: {
    uniqueId: string,
    creatorId: string,
    source: OrderSource,
    domain: OrderDomain,
    businessRef: BusinessModuleRef,
    creationDate: Dayjs,
    customer: CustomerProfile | null,
    contactDetails: ContactDetails,
    status: GenericOrderStatus,
    entries: GenericOrderEntry[],
    paymentType: PaymentType | null,
    paymentMethod: PaymentMethod | null,
    onlinePaymentDetails: OnlinePaymentDetails | null,
    offlinePaymentDetails: OfflinePaymentDetails | null,
    components: ComponentList,
    sideEffects: OrderSideEffect[],
    isGuestOrder: boolean,
    cancelledDate: Dayjs | null,
    cancelledBy: string | null,
  }): GenericOrder {
    return new GenericOrder(
        args.uniqueId,
        args.creatorId,
        args.source,
        args.domain,
        args.businessRef,
        args.creationDate,
        args.customer,
        args.contactDetails,
        args.status,
        args.entries,
        args.paymentType,
        args.paymentMethod,
        args.onlinePaymentDetails,
        args.offlinePaymentDetails,
        args.components,
        args.sideEffects,
        args.isGuestOrder,
        args.cancelledDate,
        args.cancelledBy,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): GenericOrder {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      creatorId: MapUtils.getString(map, 'creatorId'),
      source: toEnumFromString(MapUtils.getString(map, 'source'), OrderSource),
      domain: toEnumFromString(MapUtils.getString(map, 'domain'), OrderDomain),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      customer: context.fromDataOrNull<CustomerProfile>(MapUtils.getRecordOrNull(map, 'customer')),
      contactDetails: context.fromData<ContactDetails>(MapUtils.getRecord(map, 'contactDetails')),
      status: toEnumFromString(MapUtils.getString(map, '$status'), GenericOrderStatus),
      entries: MapUtils.getList<GenericOrderEntry>(map, 'entries', (value) => context.fromData<GenericOrderEntry>(JSONUtils.decodeRecord(value))),
      paymentType: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'paymentType'), PaymentType),
      paymentMethod: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'paymentMethod'), PaymentMethod),
      onlinePaymentDetails: context.fromDataOrNull<OnlinePaymentDetails>(MapUtils.getRecordOrNull(map, 'onlinePaymentDetails')),
      offlinePaymentDetails: context.fromDataOrNull<OfflinePaymentDetails>(MapUtils.getRecordOrNull(map, 'offlinePaymentDetails')),
      components: ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (value) => JSONUtils.decodeRecord(value)),
      ),
      sideEffects: MapUtils.getList<OrderSideEffect>(map, 'sideEffects', (value) => context.fromData<OrderSideEffect>(JSONUtils.decodeRecord(value))),
      isGuestOrder: MapUtils.getBoolean(map, 'isGuestOrder'),
      cancelledDate: context.decodeDateOrNull(MapUtils.getIntegerOrNull(map, 'cancelledDate')),
      cancelledBy: MapUtils.getStringOrNull(map, 'cancelledBy'),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'source': fromEnumAsString(this.source),
      'domain': fromEnumAsString(this.domain),
      'customerId': this.customerId,
      'userId': this.userId,
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'contactDetails': context.toData(this.contactDetails),
      'customer': context.toDataOrNull(this.customer),
      'entries': this.entries.map((e) => context.toData(e)),
      'components': this.components.serialize(context),
      'paymentType': fromEnumAsStringOrNull(this.paymentType),
      'paymentMethod': fromEnumAsStringOrNull(this.paymentMethod),
      'onlinePaymentDetails': context.toDataOrNull(this.onlinePaymentDetails),
      'offlinePaymentDetails': context.toDataOrNull(this.offlinePaymentDetails),
      '$status': fromEnumAsString(this.status),
      'sideEffects': this.sideEffects.map((e) => context.toData(e)),
      'isGuestOrder': this.isGuestOrder,
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
    };
  }

  clone(): this {
    return this.copyWith() as this;
  }

  copyWith({
    uniqueId = this.uniqueId,
    creatorId = this.creatorId,
    source = this.source,
    domain = this.domain,
    businessRef = cloneData(this.businessRef),
    creationDate = TimeZone.cloneDate(this.creationDate),
    customer = cloneDataOrNull(this.customer),
    contactDetails = cloneData(this.contactDetails),
    status = this.status,
    entries = cloneListOfCloneable(this.entries),
    paymentType = this.paymentType,
    paymentMethod = this.paymentMethod,
    onlinePaymentDetails = cloneDataOrNull(this.onlinePaymentDetails),
    offlinePaymentDetails = cloneDataOrNull(this.offlinePaymentDetails),
    components = cloneData(this.components),
    sideEffects = cloneListOfCloneable(this.sideEffects),
    isGuestOrder = this.isGuestOrder,
    cancelledDate = TimeZone.cloneDateOrNull(this.cancelledDate),
    cancelledBy = this.cancelledBy,
  }: {
    uniqueId?: string,
    creatorId?: string,
    eventId?: string,
    bookingStrategy?: EventBookingStrategy,
    source?: OrderSource,
    domain?: OrderDomain,
    businessRef?: BusinessModuleRef,
    creationDate?: Dayjs,
    customer?: CustomerProfile | null,
    contactDetails?: ContactDetails,
    status?: GenericOrderStatus,
    entries?: GenericOrderEntry[],
    paymentType?: PaymentType | null,
    paymentMethod?: PaymentMethod | null,
    onlinePaymentDetails?: OnlinePaymentDetails | null,
    offlinePaymentDetails?: OfflinePaymentDetails | null,
    components?: ComponentList,
    sideEffects?: OrderSideEffect[],
    isGuestOrder?: boolean,
    cancelledDate?: Dayjs | null,
    cancelledBy?: string | null,
  } = {}): this {
    return GenericOrder.create({
      uniqueId,
      creatorId,
      source,
      domain,
      businessRef,
      creationDate,
      customer,
      contactDetails,
      status,
      entries,
      paymentType,
      paymentMethod,
      onlinePaymentDetails,
      offlinePaymentDetails,
      components,
      sideEffects,
      isGuestOrder,
      cancelledDate,
      cancelledBy,
    }) as this;
  }

  copyAsAborted(): this {
    return this.copyWith({
      status: GenericOrderStatus.Aborted,
      entries: this.entries.map((e) => e.copyAsAborted()),
    });
  }

  copyAsCanceled(cancelledBy: string | null, cancelledDate: Dayjs, isRefund: boolean): this {
    return this.copyWith({
      status: isRefund ? GenericOrderStatus.Refunded : GenericOrderStatus.Canceled,
      cancelledDate: TimeZone.cloneDateOrNull(this.cancelledDate) ?? cancelledDate,
      cancelledBy: this.cancelledBy ?? cancelledBy,
      entries: this.entries.map((entry) => {
        const shouldRefund = isRefund || entry.status === GenericOrderEntryStatus.Refunded;
        return entry.copyAsCanceled(cancelledBy, cancelledDate, shouldRefund);
      }),
    });
  }

  copyWithPaymentDetails(paymentDetails: OnlinePaymentDetails): this {
    return this.copyWith({
      onlinePaymentDetails: paymentDetails,
    });
  }

  copyWithOfflinePaymentDetails(offlinePaymentDetails: OfflinePaymentDetails): this {
    return this.copyWith({
      offlinePaymentDetails: offlinePaymentDetails,
    });
  }

  copyWithPaymentStatus(status: PaymentStatus): this {
    return this.copyWith({
      onlinePaymentDetails: this.onlinePaymentDetails?.copyWith({ status }),
    });
  }

  copyWithPaymentMethod(paymentMethod: PaymentMethod): this {
    return this.copyWith({
      paymentMethod: paymentMethod,
    });
  }

  assignToCustomerAccount(account: CustomerAccountBase): this {
    return this.copyWith({
      creatorId: account.uniqueId,
      customer: this.customer?.assignToCustomerAccount(account),
    });
  }

  applyCheckoutSession({ paymentDetails, entryUpdater }: {
      paymentDetails: OnlinePaymentDetails,
      entryUpdater: (entry: GenericOrderEntry) => GenericOrderEntry,
  }): this {
    return this.copyWith({
      onlinePaymentDetails: paymentDetails,
      entries: this.entries.map((e) => entryUpdater(e)),
    });
  }

  isSame(other: GenericOrder): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.customer, this.customer) &&
        isSameData(other.contactDetails, this.contactDetails) &&
        isSameData(other.onlinePaymentDetails, this.onlinePaymentDetails) &&
        isSameList(other.entries, this.entries, isSameData) &&
        isSameList(other.sideEffects, this.sideEffects, isSameData) &&
        other.components.isSame(this.components) &&
        other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.source === this.source &&
        other.domain === this.domain &&
        other.cancelledDate === this.cancelledDate &&
        other.cancelledBy === this.cancelledBy &&
        isSameData(other.businessRef, this.businessRef) &&
        other.creationDate === this.creationDate &&
        other.status === this.status &&
        other.paymentMethod === this.paymentMethod &&
        other.paymentType === this.paymentType &&
        other.isGuestOrder === this.isGuestOrder;
  }

  copyWithOrderEntry(orderEntry: GenericOrderEntry): GenericOrder {
    return this.copyWith({
      entries: this.entries.map((entry) => {
        return entry.uniqueId === orderEntry.uniqueId ?
          orderEntry : entry.clone();
      }),
    });
  }

  findEntry(uniqueId: string | null): GenericOrderEntry | null {
    if(uniqueId == null) return null;

    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].uniqueId === uniqueId) {
        return this.entries[i];
      }
    }

    return null;
  }

  findIndexOfEntry(uniqueId: string): number {
    for(let i = 0; i < this.entries.length; i++) {
      if(this.entries[i].uniqueId === uniqueId) {
        return i;
      }
    }

    return -1;
  }

  getAllUniqueEventIds(): Set<string> {
    const uniqueEventIds = new Set<string>();

    for(let i = 0; i < this.entries.length; i++) {
      const entryEventId = this.entries[i].tryGetEventId();
      if(entryEventId != null) {
        uniqueEventIds.add(entryEventId);
      }
    }

    return uniqueEventIds;
  }

  tryGetUniqueEventId(): Optional<string> | 'multiple-events-found' {
    let eventId: string | null = null;

    for(let i = 0; i < this.entries.length; i++) {
      const entryEventId = this.entries[i].tryGetEventId();
      if(entryEventId != null) {
        if(eventId != null && entryEventId !== eventId) {
          return 'multiple-events-found';
        }

        eventId = entryEventId;
      }
    }

    return Optional.either(eventId);
  }

  tryGetCustomer(): CustomerProfile | null {
    return this.customer;
  }

  findSideEffect(effectType: OrderSideEffectType): OrderSideEffect | null {
    for(let i = 0; i < this.sideEffects.length; i++) {
      if(this.sideEffects[i].effectType === effectType) {
        return this.sideEffects[i];
      }
    }

    return null;
  }

  hasSideEffect(effectType: OrderSideEffectType): boolean {
    return this.findSideEffect(effectType) != null;
  }

  static encodedStatusSafe(status: GenericOrderStatus): string {
    return fromEnumAsString(status);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof GenericOrder;
  }

  static assertType(order: DataModel): order is GenericOrder {
    return order instanceof GenericOrder;
  }
}

export class GenericOrderBuilder extends DataModelBuilder<GenericOrder> {
  build(context: SerializationContext, map: SerializedData): GenericOrder {
    return GenericOrder.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: GenericOrder): SerializedData | null {
    let paymentStatus: PaymentStatus | null = null;

    if(model.paymentType === PaymentType.Online) {
      paymentStatus = model.onlinePaymentDetails?.status ?? null;
    }
    else {
      paymentStatus = PaymentStatus.Succeeded;
    }

    const uniqueEventIds: string[] = [];

    for(const entry of model.entries) {
      const eventId = entry.tryGetEventId();
      if(eventId != null && !uniqueEventIds.includes(eventId)) {
        uniqueEventIds.push(eventId);
      }
    }

    return {
      'paymentStatus': fromEnumAsStringOrNull(paymentStatus),
      'paymentType': fromEnumAsStringOrNull(model.paymentType),
      'paymentMethod': fromEnumAsStringOrNull(model.paymentMethod),
      'events': uniqueEventIds,
    };
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, '$status', GenericOrderStatus, GenericOrderStatus.Open);

    // Needed for deserializing invoices with a graceful creation date! See builder of [OnlinePaymentDetails].
    const creationDate = context.decodeDate(MapUtils.getInteger(map, 'creationDate'));
    context.storage.set('orderCreationDate', creationDate);

    if(versionRange.contains(GenericOrder.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(GenericOrder.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    const version = versionRange.to;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
